import * as React from "react";
import { Dropdown } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { WithModal, withModal } from "Components/General/Modal/withModal";
import { documentsTableStore } from "Pages/Project/Documents/Stores/DocumentsTableStore";
import { locateFileInDocuments } from "utils/locateFileInDocuments";
import { notificationError } from "utils/libHelpers/AntdNotification";
import { withLoadingMessage } from "utils/withLoadingMessage";
import { fileDetailsStore } from "../FileDetailsStore";
import { collectionFolderStore } from "Pages/Project/Documents/CollectionFolder/CollectionFolderStore";
import { projectsDataStore } from "Stores";
import { RouterStore } from "Config/CustomBrowserRouter";
import { ItemType } from "antd/lib/menu/interface";

type ActionData = {
  fileId?: number;
  dirId?: number;
  workflow?: number;
  task?: API_TaskData;
  question?: number;
  manageItem?: boolean;
  type?: API_FILEDATAENTITY_RELATIONS;
};
interface RelationsTableRowActionsProps extends WithTranslation, WithModal {
  data: ActionData;
  children: React.ReactNode;
  showOneFileActions?: boolean;
  showBasicFileActions?: boolean;
  showModuleFileActions?: boolean;
  showFolderActions?: boolean;
}

export const RelationsTableRowActions = withTranslation(I18N_NS.PROJECT)(
  observer(
    withModal(
      class extends React.Component<RelationsTableRowActionsProps> {
        handleMenuClick = ({ item: _, key, domEvent }) => {
          domEvent.preventDefault();
          domEvent.stopPropagation();
          const { data } = this.props;
          this.doFileClick(key, data);
        };

        get menuItems(): ItemType[] {
          const {
            t,
            data: { dirId, fileId, task, workflow, question, manageItem },
          } = this.props;
          const menuList: ItemType[] = [];

          if (fileId) {
            menuList.push(
              {
                key: "download",
                label: (
                  <>
                    <FontAwesomeIcon icon={["far", "download"]} fixedWidth={true} className="mr-5" />
                    {t("Download")}
                  </>
                ),
              },
              {
                key: "collection_folder",
                label: (
                  <>
                    <FontAwesomeIcon icon={["far", "folder-open"]} fixedWidth={true} className="mr-5" />
                    {t("Add to collection folder")}
                  </>
                ),
              }
            );
          }
          if (manageItem) {
            menuList.push({
              key: "delete_symlink",
              label: (
                <>
                  <FontAwesomeIcon icon={["far", "unlink"]} fixedWidth={true} className="mr-5" />
                  {t("Remove link")}
                </>
              ),
            });
          }
          if (dirId) {
            menuList.push({
              key: "jump_to_dir",
              label: (
                <>
                  <FontAwesomeIcon icon={["far", "folder"]} fixedWidth={true} />
                  {t("Jump to directory")}
                </>
              ),
            });
          }
          if (task) {
            menuList.push({
              key: "task",
              label: (
                <>
                  <FontAwesomeIcon icon={["far", "info-square"]} fixedWidth={true} />
                  {t("Show details")}
                </>
              ),
            });
          }
          if (workflow) {
            menuList.push({
              key: "workflow",
              label: (
                <>
                  <FontAwesomeIcon icon={["far", "info-square"]} fixedWidth={true} />
                  {t("Show details")}
                </>
              ),
            });
          }
          if (question) {
            menuList.push({
              key: "question",
              label: (
                <>
                  <FontAwesomeIcon icon={["far", "info-square"]} fixedWidth={true} />
                  {t("Show details")}
                </>
              ),
            });
          }

          return menuList;
        }

        render() {
          if (this.menuItems.length) {
            return (
              <Dropdown
                menu={{ items: this.menuItems, onClick: this.handleMenuClick, className: "documents-actions" }}
                trigger={["click"]}
              >
                {this.props.children}
              </Dropdown>
            );
          } else {
            return <span className="color-pale-grey">{this.props.children}</span>;
          }
        }

        private doFileClick = (key: string, data: ActionData) => {
          switch (key) {
            case "task": {
              const type = data.task.global ? "public" : "private";
              const { listId, id } = data.task;
              RouterStore.push(`/project/${projectsDataStore.projectId}/tasks/${type}/list/${listId}/${id}`);
              fileDetailsStore.toggleModal();
              break;
            }
            case "workflow":
              RouterStore.push(`/project/${projectsDataStore.projectId}/workflows/admin/workflow/${data.workflow}`);
              fileDetailsStore.toggleModal();
              break;
            case "download":
              withLoadingMessage(() => documentsTableStore.downloadRelationFile(data.fileId));
              break;
            case "jump_to_dir":
              locateFileInDocuments(data.dirId, data.fileId);
              fileDetailsStore.hideModal();
              break;
            case "collection_folder":
              (async () => {
                const file = await documentsTableStore.getFileById(data.dirId, data.fileId);
                collectionFolderStore.addFile(file);
              })();
              break;
            case "delete_symlink":
              fileDetailsStore.deleteSymlink(data.fileId, data.dirId);
              break;
            default:
              notificationError("Error", `Unknown action {${key}}`);
              break;
          }
        };
      }
    )
  )
);
